/**
 * @typedef {import('../Agent/Contacts/EditContactState').default['initialValues']['properties'][0]} ContactProperty
 */

import {
	default as FinaceWidgets,
	STANDARD_FINANCE_WIDGET_CONTAINER_CLASS_NAMES,
	STANDARD_FINANCE_WIDGET_GRID_CLASS_NAME,
	STANDARD_FINANCE_WIDGET_KEYS,
} from './FinaceWidgets';
import {
	buildFinanceWidgetConfiguration,
	calculateEstimatedCurrentBalance,
	calculateEstimatedCurrentEquity,
	calculateFinanceWidgetMonthlyPayment,
	convertFinanceWidgetCentsToDollars,
	getLatestQuantariumValueInDollars,
} from './FinanceWidgets/financeWidgetAttributes';
import { translate } from '../Internationalization';
import { updateContactPropertyMortgage } from '../Agent/agent-service';
import BaseButton from '../../shared/BaseButton/BaseButton';
import captureError from '../../utils/captureError';
import FormErrorMessages from '../../shared/Forms/Messages/FormErrorMessages';
import Loading from '../../shared/Loading';
import NoContent from '../../shared/NoContent';
import paths from '../../config/local/paths';
import PropertyDropdownSelector from './PropertyDropdownSelector';
import PropertyValuationSummary from '../Homeowner/Finances/PropertyValuationSummary';
import React from 'react';
import UpgradeOverlay from './UpgradeOverlay';

/**
 * @typedef {{
 * 	amountToLoan: number | null,
 * 	amortizationYears: number | null,
 * 	escrowInsuranceAmount: number | null,
 * 	escrowInsuranceFrequency: 'annual' | 'monthly',
 * 	escrowInsuranceMonthly: number | null,
 * 	escrowTaxAmount: number | null,
 * 	escrowTaxFrequency: 'annual' | 'monthly',
 * 	escrowTaxMonthly: number | null,
 * 	originalDateOfContract: Date | null,
 * 	rate: number | null
 * }} MortgageDetails
 */

/**
 * @typedef {{
 * 	amountToLoan: string,
 * 	amortizationYears: string,
 * 	escrowInsurance: string,
 * 	escrowInsuranceFrequency: 'annual' | 'monthly',
 * 	escrowTax: string,
 * 	escrowTaxFrequency: 'annual' | 'monthly',
 * 	originalDateOfContract: string,
 * 	rate: string
 * }} MortgageDraft
 */

/**
 * @typedef {{
 * 	accessToken: string,
 * 	agentId: string,
 * 	canViewAvms?: boolean,
 * 	canViewFinanceWidgets?: boolean,
 * 	canViewHkAvm?: boolean,
 * 	canViewMortgage?: boolean,
 * 	canViewQuantariumAvm?: boolean,
 * 	contactId: string,
 * 	hidePropertySelector?: boolean,
 * 	homeownerId: string | null,
 * 	isGettingProperties: boolean,
 * 	onBack?: () => void,
 * 	onMortgageSaved: (propertyId: string, mortgageDetails: MortgageDetails) => Promise<void> | void,
 * 	onPropertySelect: (propertyId: string) => void,
 * 	properties: ContactProperty[],
 * 	selectedPropertyId: string,
 * 	subscriptionPath?: string,
 * 	subscriptionTier: number,
 * 	userId: string
 * }} ContactFinancesAndValuationProps
 */

const STARTER_TIER = 1;
const STANDARD_TIER = 2;

/**
 * @typedef {{
 * 	isEditingMortgage: boolean,
 * 	isSavingMortgage: boolean,
 * 	mortgageDraft: MortgageDraft | null,
 * 	mortgageErrorMessage: string | null
 * }} ContactFinancesAndValuationState
 */

/**
 * @extends {React.Component<ContactFinancesAndValuationProps, ContactFinancesAndValuationState>}
 */
export default class ContactFinancesAndValuation extends React.Component {
	/** @param {ContactFinancesAndValuationProps} props */
	constructor(props) {
		super(props);

		this.state = {
			isEditingMortgage: false,
			isSavingMortgage: false,
			mortgageDraft: null,
			mortgageErrorMessage: null,
		};

		this.handleCancelMortgageEdit =
			this.#handleCancelMortgageEdit.bind(this);
		this.handleMortgageDraftChange =
			this.#handleMortgageDraftChange.bind(this);
		this.handleSaveMortgage = this.#handleSaveMortgage.bind(this);
		this.handleStartMortgageEdit = this.#handleStartMortgageEdit.bind(this);
	}

	/**
	 * @returns {ContactProperty | undefined}
	 */
	#getSelectedProperty() {
		return this.props.properties.find(
			(property) => property.id === this.props.selectedPropertyId
		);
	}

	/**
	 * @param {ContactProperty} property
	 * @returns {string}
	 */
	static #formatPropertyAddress(property) {
		const address = [
			property.address.streetAddress1,
			property.address.streetAddress2,
			property.address.city,
			property.address.state,
			property.address.postalCode,
			property.address.country,
		]
			.filter(Boolean)
			.join(', ');

		return address || property.title || 'Property';
	}

	/**
	 * @param {ContactProperty | undefined} property
	 * @returns {Map<string, ContactProperty['valuations']> | null}
	 */
	static #getValuationMap(property) {
		if (!property?.valuations.length) {
			return null;
		}

		const valuationMap = new Map();

		property.valuations.forEach((valuation) => {
			const provider = valuation.provider;
			const providerValuations = valuationMap.get(provider) ?? [];
			providerValuations.push(valuation);
			valuationMap.set(provider, providerValuations);
		});

		return valuationMap;
	}

	/**
	 * @param {Map<string, ContactProperty['valuations']> | null} valuationMap
	 * @param {boolean} canViewQuantariumAvm
	 * @param {boolean} canViewHkAvm
	 * @returns {Map<string, ContactProperty['valuations']>}
	 */
	static #filterValuationMap(
		valuationMap,
		canViewQuantariumAvm,
		canViewHkAvm
	) {
		const filteredValuations = new Map();

		if (canViewQuantariumAvm && valuationMap?.has('quantarium')) {
			filteredValuations.set(
				'quantarium',
				valuationMap.get('quantarium')
			);
		}

		if (canViewHkAvm && valuationMap?.has('quantariumPrivateLabel')) {
			filteredValuations.set(
				'quantariumPrivateLabel',
				valuationMap.get('quantariumPrivateLabel')
			);
		}

		return filteredValuations;
	}

	/**
	 * @param {ContactProperty['valuations']} valuations
	 * @returns {{max: number, min: number} | null}
	 */
	static #getValuationRange(valuations) {
		/** @type {number[]} */
		const range = valuations.reduce((values, valuation) => {
			if (valuation.valueLow !== null) {
				values.push(valuation.valueLow);
			}

			if (valuation.valueHigh !== null) {
				values.push(valuation.valueHigh);
			}

			return values;
		}, /** @type {number[]} */ ([]));

		if (!range.length) {
			return null;
		}

		return {
			max: Math.max(...range),
			min: Math.min(...range),
		};
	}

	/**
	 * @param {number | null | undefined} value
	 * @returns {string}
	 */
	static #formatMortgageCurrency(value) {
		return value === null || value === undefined
			? ''
			: value.toLocaleString('en-US', {
					currency: 'USD',
					maximumFractionDigits: 0,
					style: 'currency',
			  });
	}

	/**
	 * @param {Date | string | null | undefined} value
	 * @returns {Date | null}
	 */
	static #normalizeMortgageDate(value) {
		if (!value) {
			return null;
		}

		const date = value instanceof Date ? value : new Date(value);

		if (Number.isNaN(date.valueOf()) || date.valueOf() === 0) {
			return null;
		}

		return date;
	}

	/**
	 * @param {Date | string | null | undefined} value
	 * @returns {string}
	 */
	static #formatMortgageDisplayDate(value) {
		const date = ContactFinancesAndValuation.#normalizeMortgageDate(value);

		return date ? date.toLocaleDateString('en-US') : '';
	}

	/**
	 * @param {Date | string | null | undefined} value
	 * @returns {string}
	 */
	static #formatMortgageDateForInput(value) {
		const date = ContactFinancesAndValuation.#normalizeMortgageDate(value);

		if (!date) {
			return '';
		}

		return [
			date.getFullYear(),
			String(date.getMonth() + 1).padStart(2, '0'),
			String(date.getDate()).padStart(2, '0'),
		].join('-');
	}

	/**
	 * @param {string} value
	 * @returns {Date | null}
	 */
	static #parseMortgageDateInput(value) {
		if (!value) {
			return null;
		}

		const date = new Date(`${value}T00:00:00`);

		return Number.isNaN(date.valueOf()) ? null : date;
	}

	/**
	 * @param {string} value
	 * @returns {number | null}
	 */
	static #parseMortgageNumberInput(value) {
		if (value === '') {
			return null;
		}

		const number = Number(value);

		return Number.isNaN(number) ? null : number;
	}

	/**
	 * @param {number | null | undefined} value
	 * @returns {string}
	 */
	static #formatMortgageNumberInput(value) {
		return value === null || value === undefined ? '' : String(value);
	}

	/**
	 * @param {number} value
	 * @returns {number}
	 */
	static #roundMortgageAmount(value) {
		return Math.round((value + Number.EPSILON) * 100) / 100;
	}

	/**
	 * @param {number | null | undefined} amount
	 * @param {number | null | undefined} monthlyAmount
	 * @param {'annual' | 'monthly'} frequency
	 * @returns {string}
	 */
	static #formatEscrowDraftAmount(amount, monthlyAmount, frequency) {
		if (amount !== null && amount !== undefined) {
			return ContactFinancesAndValuation.#formatMortgageNumberInput(
				amount
			);
		}

		if (monthlyAmount === null || monthlyAmount === undefined) {
			return '';
		}

		const displayAmount =
			frequency === 'annual' ? monthlyAmount * 12 : monthlyAmount;

		return ContactFinancesAndValuation.#formatMortgageNumberInput(
			ContactFinancesAndValuation.#roundMortgageAmount(displayAmount)
		);
	}

	/**
	 * @param {number | null | undefined} value
	 * @returns {number}
	 */
	static #getMortgageMonthlyAmount(value) {
		return value ?? 0;
	}

	/**
	 * @returns {'annual' | 'monthly'}
	 */
	static #getDefaultEscrowFrequency() {
		return 'monthly';
	}

	/**
	 * @param {number | null | undefined} value
	 * @param {'annual' | 'monthly'} frequency
	 * @returns {number | null}
	 */
	static #normalizeEscrowMonthlyAmount(value, frequency) {
		if (value === null || value === undefined) {
			return null;
		}

		const monthlyValue = frequency === 'annual' ? value / 12 : value;

		return ContactFinancesAndValuation.#roundMortgageAmount(monthlyValue);
	}

	/**
	 * @param {Date | null} value
	 * @returns {string | null}
	 */
	static #formatMortgageDateForSaving(value) {
		return value
			? ContactFinancesAndValuation.#formatMortgageDateForInput(value)
			: null;
	}

	/**
	 * @param {ContactProperty} property
	 * @returns {MortgageDraft}
	 */
	static #createMortgageDraft(property) {
		const details = property.details;
		const escrowInsuranceFrequency =
			details.escrowInsuranceFrequency ??
			ContactFinancesAndValuation.#getDefaultEscrowFrequency();
		const escrowTaxFrequency =
			details.escrowTaxFrequency ??
			ContactFinancesAndValuation.#getDefaultEscrowFrequency();

		return {
			amortizationYears:
				ContactFinancesAndValuation.#formatMortgageNumberInput(
					details.amortizationYears ?? 30
				),
			amountToLoan:
				ContactFinancesAndValuation.#formatMortgageNumberInput(
					details.amountToLoan
				),
			escrowInsurance:
				ContactFinancesAndValuation.#formatEscrowDraftAmount(
					details.escrowInsuranceAmount,
					details.escrowInsuranceMonthly,
					escrowInsuranceFrequency
				),
			escrowInsuranceFrequency,
			escrowTax: ContactFinancesAndValuation.#formatEscrowDraftAmount(
				details.escrowTaxAmount,
				details.escrowTaxMonthly,
				escrowTaxFrequency
			),
			escrowTaxFrequency,
			originalDateOfContract:
				ContactFinancesAndValuation.#formatMortgageDateForInput(
					details.originalDateOfContract
				),
			rate: ContactFinancesAndValuation.#formatMortgageNumberInput(
				details.rate
			),
		};
	}

	/**
	 * @param {MortgageDraft} draft
	 * @returns {MortgageDetails}
	 */
	static #mapMortgageDraftToDetails(draft) {
		const escrowInsurance =
			ContactFinancesAndValuation.#parseMortgageNumberInput(
				draft.escrowInsurance
			);
		const escrowTax = ContactFinancesAndValuation.#parseMortgageNumberInput(
			draft.escrowTax
		);

		return {
			amortizationYears:
				ContactFinancesAndValuation.#parseMortgageNumberInput(
					draft.amortizationYears
				),
			amountToLoan: ContactFinancesAndValuation.#parseMortgageNumberInput(
				draft.amountToLoan
			),
			escrowInsuranceAmount:
				escrowInsurance === null
					? null
					: ContactFinancesAndValuation.#roundMortgageAmount(
							escrowInsurance
					  ),
			escrowInsuranceFrequency: draft.escrowInsuranceFrequency,
			escrowInsuranceMonthly:
				ContactFinancesAndValuation.#normalizeEscrowMonthlyAmount(
					escrowInsurance,
					draft.escrowInsuranceFrequency
				),
			escrowTaxAmount:
				escrowTax === null
					? null
					: ContactFinancesAndValuation.#roundMortgageAmount(
							escrowTax
					  ),
			escrowTaxFrequency: draft.escrowTaxFrequency,
			escrowTaxMonthly:
				ContactFinancesAndValuation.#normalizeEscrowMonthlyAmount(
					escrowTax,
					draft.escrowTaxFrequency
				),
			originalDateOfContract:
				ContactFinancesAndValuation.#parseMortgageDateInput(
					draft.originalDateOfContract
				),
			rate: ContactFinancesAndValuation.#parseMortgageNumberInput(
				draft.rate
			),
		};
	}

	/**
	 * @returns {void}
	 */
	#handleStartMortgageEdit() {
		const selectedProperty = this.#getSelectedProperty();

		if (!selectedProperty) {
			return;
		}

		this.setState({
			isEditingMortgage: true,
			mortgageDraft:
				ContactFinancesAndValuation.#createMortgageDraft(
					selectedProperty
				),
			mortgageErrorMessage: null,
		});
	}

	/**
	 * @returns {void}
	 */
	#handleCancelMortgageEdit() {
		this.setState({
			isEditingMortgage: false,
			mortgageDraft: null,
			mortgageErrorMessage: null,
		});
	}

	/**
	 * @param {React.ChangeEvent<HTMLInputElement | HTMLSelectElement>} event
	 * @returns {void}
	 */
	#handleMortgageDraftChange(event) {
		const { name, value } = event.currentTarget;

		this.setState((prevState) => ({
			mortgageDraft: prevState.mortgageDraft
				? {
						...prevState.mortgageDraft,
						[name]: value,
				  }
				: null,
		}));
	}

	/**
	 * @returns {Promise<void>}
	 */
	async #handleSaveMortgage() {
		const selectedProperty = this.#getSelectedProperty();
		const draft = this.state.mortgageDraft;

		if (!selectedProperty || !draft) {
			return;
		}

		const mortgageDetails =
			ContactFinancesAndValuation.#mapMortgageDraftToDetails(draft);

		this.setState({
			isSavingMortgage: true,
			mortgageErrorMessage: null,
		});

		try {
			await updateContactPropertyMortgage(
				this.props.contactId,
				selectedProperty.id,
				{
					...mortgageDetails,
					originalDateOfContract:
						ContactFinancesAndValuation.#formatMortgageDateForSaving(
							mortgageDetails.originalDateOfContract
						),
				},
				this.props.accessToken
			);

			await this.props.onMortgageSaved(
				selectedProperty.id,
				mortgageDetails
			);

			this.setState({
				isEditingMortgage: false,
				isSavingMortgage: false,
				mortgageDraft: null,
			});
		} catch (error) {
			captureError(error);
			this.setState({
				isSavingMortgage: false,
				mortgageErrorMessage: translate('global.error'),
			});
		}
	}

	/**
	 * @param {ContactProperty | undefined} selectedProperty
	 * @returns {number | null}
	 */
	static #getAverageValuationValue(selectedProperty) {
		const valuationRange = ContactFinancesAndValuation.#getValuationRange(
			selectedProperty?.valuations ?? []
		);

		if (valuationRange !== null) {
			return convertFinanceWidgetCentsToDollars(
				(valuationRange.min + valuationRange.max) / 2
			);
		}

		return convertFinanceWidgetCentsToDollars(
			selectedProperty?.valuations[0]?.valueMean
		);
	}

	/**
	 * @param {ContactProperty | undefined} selectedProperty
	 * @returns {JSX.Element | null}
	 */
	#renderFinanceWidgets(selectedProperty) {
		if (!selectedProperty) {
			return null;
		}

		const configuration = buildFinanceWidgetConfiguration({
			accessToken: this.props.accessToken,
			agentId: this.props.agentId,
			details: selectedProperty.details,
			estimatedHomeValue:
				ContactFinancesAndValuation.#getAverageValuationValue(
					selectedProperty
				),
			userId: this.props.userId,
			userType: 'agent',
		});

		return (
			<FinaceWidgets
				attributes={configuration.attributes}
				gridClassName={STANDARD_FINANCE_WIDGET_GRID_CLASS_NAME}
				reloadKey={`${selectedProperty.id}:${configuration.reloadKey}`}
				title="Finance Tools"
				widgetContainerClassNames={
					STANDARD_FINANCE_WIDGET_CONTAINER_CLASS_NAMES
				}
				widgetKeys={STANDARD_FINANCE_WIDGET_KEYS}
			/>
		);
	}

	/**
	 * @returns {JSX.Element | null}
	 */
	#renderPropertySelector() {
		if (this.props.hidePropertySelector) {
			return null;
		}

		if (!this.props.properties.length) {
			return null;
		}

		return (
			<section className="contact-property-selector">
				<PropertyDropdownSelector
					className="fancy-dropdown"
					formatPropertyLabel={
						/** @param {ContactProperty} property */
						(property) =>
							ContactFinancesAndValuation.#formatPropertyAddress(
								property
							)
					}
					idPrefix="contact-property-selector"
					onSelect={this.props.onPropertySelect}
					propertyList={this.props.properties}
					selectedProperty={this.#getSelectedProperty()}
				/>
			</section>
		);
	}

	/**
	 * @param {string} [className]
	 * @returns {JSX.Element}
	 */
	static #renderSectionLoader(className = '') {
		return (
			<div
				className={`flex min-h-40 items-center justify-center ${className}`}
			>
				<Loading />
			</div>
		);
	}

	/**
	 * @param {string} header
	 * @param {string | null | undefined} footer
	 * @param {boolean} [isFullWidth]
	 * @returns {JSX.Element}
	 */
	static #renderMortgageItem(header, footer, isFullWidth = false) {
		return (
			<div
				className={`property-detail-item ${
					isFullWidth ? 'width-full' : ''
				}`}
				key={header}
			>
				<dt className="property-detail-item-header">{header}</dt>
				<dd className="property-detail-item-footer">
					{footer ||
						translate(
							'homeowner.pages.dashboard.properties.missing_datum'
						)}
				</dd>
			</div>
		);
	}

	/**
	 * @param {string} header
	 * @param {string} name
	 * @param {string} value
	 * @param {'date' | 'number'} type
	 * @param {string} [suffix]
	 * @returns {JSX.Element}
	 */
	#renderMortgageFormField(header, name, value, type, suffix = '') {
		return (
			<div className="mortgage-form-field min-w-0" key={header}>
				<label
					className="mortgage-form-label block h-ms mb-2 text-grey-900"
					htmlFor={name}
				>
					{header}
				</label>
				<div className="mortgage-form-control flex items-center gap-2 min-w-0">
					<input
						className="input mortgage-form-input min-w-0 w-full"
						id={name}
						inputMode={type === 'number' ? 'decimal' : undefined}
						min={type === 'number' ? '0' : undefined}
						name={name}
						onChange={this.handleMortgageDraftChange}
						step={type === 'number' ? '0.01' : undefined}
						type={type === 'number' ? 'text' : type}
						value={value}
					/>
					{suffix ? (
						<span className="mortgage-form-suffix hl-ms whitespace-nowrap">
							{suffix}
						</span>
					) : null}
				</div>
			</div>
		);
	}

	/**
	 * @param {string} header
	 * @param {string} name
	 * @param {string} value
	 * @param {'annual' | 'monthly'} frequency
	 * @returns {JSX.Element}
	 */
	#renderMortgageEscrowField(header, name, value, frequency) {
		const frequencyName = `${name}Frequency`;

		return (
			<div
				className="mortgage-form-field mortgage-form-field-compact min-w-0"
				key={header}
			>
				<label
					className="mortgage-form-label block h-ms mb-2 text-grey-900"
					htmlFor={name}
				>
					{header}
				</label>
				<div className="mortgage-form-control flex items-center gap-2 min-w-0">
					<input
						className="input mortgage-form-input min-w-0 w-full"
						id={name}
						inputMode="decimal"
						min="0"
						name={name}
						onChange={this.handleMortgageDraftChange}
						step="0.01"
						type="text"
						value={value}
					/>
					<select
						className="input mortgage-form-select min-w-0 w-36"
						name={frequencyName}
						onChange={this.handleMortgageDraftChange}
						value={frequency}
					>
						<option value="monthly">{'Monthly'}</option>
						<option value="annual">{'Annual'}</option>
					</select>
				</div>
			</div>
		);
	}

	/**
	 * @param {string} header
	 * @param {string | null | undefined} value
	 * @returns {JSX.Element}
	 */
	static #renderMortgageDisplayField(header, value) {
		return (
			<div className="mortgage-form-field min-w-0" key={header}>
				<div className="mortgage-form-label block h-ms mb-2 text-grey-900">
					{header}
				</div>
				<div className="mortgage-form-readonly min-h-10 border border-grey-100 rounded px-3 py-2 hl-ms">
					{value ||
						translate(
							'homeowner.pages.dashboard.properties.missing_datum'
						)}
				</div>
			</div>
		);
	}

	/**
	 * @param {MortgageDraft} draft
	 * @param {{amortizationPeriod: string, amountToLoan: string, escrowInsurance: string, escrowTax: string, estimatedPrincipalInterest: string, interestRate: string, originalDateOfContract: string, totalPayment: string, years: string}} labels
	 * @param {string | null} estimatedPrincipalAndInterest
	 * @param {string | null} totalPayment
	 * @returns {JSX.Element}
	 */
	#renderMortgageEditForm(
		draft,
		labels,
		estimatedPrincipalAndInterest,
		totalPayment
	) {
		return (
			<div className="card-body mortgage-edit-form grid gap-x-8 gap-y-5 text-left md:grid-cols-2">
				{this.#renderMortgageFormField(
					labels.amountToLoan,
					'amountToLoan',
					draft.amountToLoan,
					'number'
				)}
				{this.#renderMortgageFormField(
					labels.originalDateOfContract,
					'originalDateOfContract',
					draft.originalDateOfContract,
					'date'
				)}
				{this.#renderMortgageFormField(
					labels.interestRate,
					'rate',
					draft.rate,
					'number',
					'%'
				)}
				{this.#renderMortgageFormField(
					labels.amortizationPeriod,
					'amortizationYears',
					draft.amortizationYears,
					'number',
					labels.years
				)}
				{ContactFinancesAndValuation.#renderMortgageDisplayField(
					labels.estimatedPrincipalInterest,
					estimatedPrincipalAndInterest
				)}
				{this.#renderMortgageEscrowField(
					labels.escrowTax,
					'escrowTax',
					draft.escrowTax,
					draft.escrowTaxFrequency
				)}
				{this.#renderMortgageEscrowField(
					labels.escrowInsurance,
					'escrowInsurance',
					draft.escrowInsurance,
					draft.escrowInsuranceFrequency
				)}
				{ContactFinancesAndValuation.#renderMortgageDisplayField(
					labels.totalPayment,
					totalPayment
				)}
			</div>
		);
	}

	/**
	 * @param {ContactProperty | undefined} selectedProperty
	 * @returns {JSX.Element | null}
	 */
	// eslint-disable-next-line complexity
	#renderMortgageInformation(selectedProperty) {
		if (!selectedProperty) {
			return null;
		}

		const details = selectedProperty.details;
		const draft = this.state.mortgageDraft;
		const mortgageDetails =
			this.state.isEditingMortgage && draft
				? {
						...details,
						...ContactFinancesAndValuation.#mapMortgageDraftToDetails(
							draft
						),
				  }
				: details;
		const amortizationYears = mortgageDetails.amortizationYears ?? 30;
		const estimatedPrincipalAndInterest =
			calculateFinanceWidgetMonthlyPayment(
				mortgageDetails.amountToLoan,
				mortgageDetails.rate,
				amortizationYears
			);
		const totalPayment =
			estimatedPrincipalAndInterest === null
				? null
				: estimatedPrincipalAndInterest +
				  ContactFinancesAndValuation.#getMortgageMonthlyAmount(
						mortgageDetails.escrowTaxMonthly
				  ) +
				  ContactFinancesAndValuation.#getMortgageMonthlyAmount(
						mortgageDetails.escrowInsuranceMonthly
				  );
		const estimatedCurrentBalance =
			calculateEstimatedCurrentBalance(mortgageDetails);
		const estimatedCurrentEquity = calculateEstimatedCurrentEquity(
			getLatestQuantariumValueInDollars(
				ContactFinancesAndValuation.#getValuationMap(selectedProperty)
			),
			estimatedCurrentBalance
		);
		const labels = {
			amortizationPeriod: translate(
				'agent.pages.contacts.edit.finances_valuation.mortgage.amortization_period'
			),
			escrowInsurance: translate(
				'agent.pages.contacts.edit.finances_valuation.mortgage.escrow_insurance'
			),
			escrowTax: translate(
				'agent.pages.contacts.edit.finances_valuation.mortgage.escrow_tax'
			),
			estimatedCurrentBalance: translate(
				'agent.pages.contacts.edit.finances_valuation.mortgage.estimated_current_balance'
			),
			estimatedCurrentEquity: translate(
				'agent.pages.contacts.edit.finances_valuation.mortgage.estimated_current_equity'
			),
			estimatedPrincipalInterest: translate(
				'agent.pages.contacts.edit.finances_valuation.mortgage.estimated_principal_interest'
			),
			interestRate: translate(
				'agent.pages.contacts.edit.finances_valuation.mortgage.interest_rate'
			),
			originalAmount: translate(
				'agent.pages.contacts.edit.finances_valuation.mortgage.original_amount'
			),
			originationDate: translate(
				'agent.pages.contacts.edit.finances_valuation.mortgage.origination_date'
			),
			totalPayment: translate(
				'agent.pages.contacts.edit.finances_valuation.mortgage.total_payment'
			),
			years: translate(
				'agent.pages.contacts.edit.finances_valuation.mortgage.years'
			),
		};
		const editLabels = {
			...labels,
			amountToLoan: labels.originalAmount,
			originalDateOfContract: labels.originationDate,
		};
		const readOnlyItems = [
			{
				footer: ContactFinancesAndValuation.#formatMortgageCurrency(
					mortgageDetails.amountToLoan
				),
				header: labels.originalAmount,
			},
			{
				footer: ContactFinancesAndValuation.#formatMortgageDisplayDate(
					mortgageDetails.originalDateOfContract
				),
				header: labels.originationDate,
			},
			{
				footer: ContactFinancesAndValuation.#formatMortgageCurrency(
					estimatedCurrentBalance
				),
				header: labels.estimatedCurrentBalance,
			},
			{
				footer:
					mortgageDetails.rate === null
						? null
						: `${mortgageDetails.rate}%`,
				header: labels.interestRate,
			},
			{
				footer: ContactFinancesAndValuation.#formatMortgageCurrency(
					estimatedCurrentEquity
				),
				header: labels.estimatedCurrentEquity,
			},
			{
				footer: `${amortizationYears} ${labels.years}`,
				header: labels.amortizationPeriod,
			},
		];
		const items = [
			...readOnlyItems,
			{
				footer: ContactFinancesAndValuation.#formatMortgageCurrency(
					estimatedPrincipalAndInterest
				),
				header: labels.estimatedPrincipalInterest,
			},
			{
				footer: ContactFinancesAndValuation.#formatMortgageCurrency(
					mortgageDetails.escrowTaxMonthly
				),
				header: labels.escrowTax,
			},
			{
				footer: ContactFinancesAndValuation.#formatMortgageCurrency(
					mortgageDetails.escrowInsuranceMonthly
				),
				header: labels.escrowInsurance,
			},
			{
				footer: ContactFinancesAndValuation.#formatMortgageCurrency(
					totalPayment
				),
				header: labels.totalPayment,
			},
		];

		return (
			<section className="mt-10">
				<header className="mb-3 flex items-center justify-between gap-3">
					<h2 className="h-ms-4">
						{translate(
							'homeowner.pages.finances.view.mortgage.title'
						)}
					</h2>
					{this.state.isEditingMortgage ? (
						<div className="flex gap-2">
							<BaseButton
								disabled={this.state.isSavingMortgage}
								onClick={this.handleSaveMortgage}
								size="small"
							>
								{this.state.isSavingMortgage
									? 'Saving...'
									: translate(
											'global.forms.buttons.save_text'
									  )}
							</BaseButton>
							<BaseButton
								disabled={this.state.isSavingMortgage}
								onClick={this.handleCancelMortgageEdit}
								outline={true}
								size="small"
							>
								{translate('global.forms.buttons.cancel')}
							</BaseButton>
						</div>
					) : (
						<BaseButton
							onClick={this.handleStartMortgageEdit}
							size="small"
						>
							{translate(
								'homeowner.pages.finances.view.mortgage.edit_details'
							)}
						</BaseButton>
					)}
				</header>
				{this.state.mortgageErrorMessage ? (
					<FormErrorMessages
						messages={this.state.mortgageErrorMessage}
					/>
				) : null}
				<section className="card card-property-detail card-border">
					{this.state.isEditingMortgage && draft ? (
						this.#renderMortgageEditForm(
							draft,
							editLabels,
							ContactFinancesAndValuation.#formatMortgageCurrency(
								estimatedPrincipalAndInterest
							),
							ContactFinancesAndValuation.#formatMortgageCurrency(
								totalPayment
							)
						)
					) : (
						<dl className="card-body property-details grid gap-2 sm:grid-cols-2 lg:grid-cols-4 text-start">
							{items.map((item) =>
								ContactFinancesAndValuation.#renderMortgageItem(
									item.header,
									item.footer
								)
							)}
						</dl>
					)}
				</section>
			</section>
		);
	}

	/**
	 * @returns {JSX.Element}
	 */
	static #renderLoadingSections() {
		return (
			<React.Fragment>
				<section className="mt-4">
					<h2 className="h-ms-4 mb-6">
						{translate(
							'homeowner.pages.finances.view.estimate.title'
						)}
					</h2>
					<section className="card card-border">
						{ContactFinancesAndValuation.#renderSectionLoader()}
					</section>
				</section>

				<section className="mt-10">
					<header className="mb-3 flex items-center justify-between gap-3">
						<h2 className="h-ms-4">
							{translate(
								'homeowner.pages.finances.view.mortgage.title'
							)}
						</h2>
					</header>
					<section className="card card-property-detail card-border">
						{ContactFinancesAndValuation.#renderSectionLoader()}
					</section>
				</section>

				<section className="mt-10">
					<h2 className="h-ms-4 mb-6">{'Finance Tools'}</h2>
					<section className="card card-border">
						{ContactFinancesAndValuation.#renderSectionLoader(
							'min-h-60'
						)}
					</section>
				</section>
			</React.Fragment>
		);
	}

	/**
	 * @param {number} requiredTier
	 * @returns {boolean}
	 */
	#hasTier(requiredTier) {
		return (this.props.subscriptionTier ?? 0) >= requiredTier;
	}

	/** @returns {string} */
	#getSubscriptionPath() {
		return this.props.subscriptionPath ?? paths.app.agent.subscription.root;
	}

	/**
	 * @param {string} title
	 * @param {React.ReactNode[]} messages
	 * @returns {JSX.Element}
	 */
	#renderUpgradeOverlay(title, messages) {
		return (
			<UpgradeOverlay
				buttonText={translate(
					'agent.pages.contacts.view.upgrade_table.upgrade'
				)}
				messages={messages}
				title={title}
				to={this.#getSubscriptionPath()}
			/>
		);
	}

	/** @returns {JSX.Element} */
	#renderHkAvmUpgradeCard() {
		return (
			<UpgradeOverlay
				buttonText={translate(
					'agent.pages.contacts.view.upgrade_table.upgrade'
				)}
				messages={[
					'HomeKeepr property valuation is available on Standard plans and above.',
				]}
				title="Upgrade to view HomeKeepr valuation"
				to={this.#getSubscriptionPath()}
			/>
		);
	}

	/**
	 * @param {string} providerName
	 * @returns {JSX.Element}
	 */
	static #renderAvmNoContentCard(providerName) {
		return (
			<section className="card card-border flex flex-col justify-between h-full p-8">
				<header className="mb-6">
					<p className="text-sm uppercase text-grey-700 mb-2">
						{providerName}
					</p>
					<h3 className="h-ms-1">
						{translate(
							'homeowner.pages.finances.view.estimate.today_value'
						)}
					</h3>
				</header>

				<NoContent />
			</section>
		);
	}

	/** @returns {JSX.Element} */
	#renderQuantariumAvmUpgradeCard() {
		return (
			<UpgradeOverlay
				buttonText={translate(
					'agent.pages.contacts.view.upgrade_table.upgrade'
				)}
				messages={[
					'Quantarium property valuation is available on Starter plans and above.',
				]}
				title="Upgrade to view Quantarium valuation"
				to={this.#getSubscriptionPath()}
			/>
		);
	}

	/** @returns {JSX.Element} */
	#renderMortgageUpgradeSection() {
		return (
			<section className="mt-10">
				<h2 className="h-ms-4">
					{translate('homeowner.pages.finances.view.mortgage.title')}
				</h2>
				{this.#renderUpgradeOverlay(
					'Upgrade to view mortgage information',
					[
						'Mortgage information is available on Starter plans and above.',
					]
				)}
			</section>
		);
	}

	/** @returns {JSX.Element} */
	#renderFinanceWidgetsUpgradeSection() {
		return (
			<section className="mt-10">
				<h2 className="h-ms-4 mb-6">{'Finance Tools'}</h2>
				{this.#renderUpgradeOverlay('Upgrade to view finance tools', [
					'Finance tools are available on Standard plans and above.',
				])}
			</section>
		);
	}

	/** @returns {JSX.Element | null} */
	#renderHeaderAction() {
		if (this.props.onBack) {
			return (
				<BaseButton
					onClick={this.props.onBack}
					outline={true}
					size="small"
				>
					{'Back'}
				</BaseButton>
			);
		}

		if (!this.props.isGettingProperties) {
			return this.#renderPropertySelector();
		}

		return null;
	}

	/**
	 * @param {Map<string, ContactProperty['valuations']>} valuations
	 * @param {ContactProperty | undefined} selectedProperty
	 * @param {boolean} canViewQuantariumAvm
	 * @param {boolean} canViewHkAvm
	 * @returns {JSX.Element | null}
	 */
	#renderPropertyValuationSection(
		valuations,
		selectedProperty,
		canViewQuantariumAvm,
		canViewHkAvm
	) {
		if (!selectedProperty) {
			return null;
		}

		const hasQuantariumValuation = valuations.has('quantarium');
		const hasHkValuation = valuations.has('quantariumPrivateLabel');

		return (
			<section className="mt-4">
				<h2 className="h-ms-4 mb-6">
					{translate('homeowner.pages.finances.view.estimate.title')}
				</h2>
				<PropertyValuationSummary
					isRequestingAccurateValuation={false}
					onRequestAccurateValuation={() => null}
					requestStatus={null}
					valuations={valuations}
				>
					{canViewQuantariumAvm && !hasQuantariumValuation
						? ContactFinancesAndValuation.#renderAvmNoContentCard(
								'Quantarium'
						  )
						: null}
					{canViewQuantariumAvm
						? null
						: this.#renderQuantariumAvmUpgradeCard()}
					{canViewHkAvm && !hasHkValuation
						? ContactFinancesAndValuation.#renderAvmNoContentCard(
								'HomeKeepr'
						  )
						: null}
					{canViewHkAvm ? null : this.#renderHkAvmUpgradeCard()}
				</PropertyValuationSummary>
			</section>
		);
	}

	/**
	 * @returns {JSX.Element | null}
	 */
	render() {
		const selectedProperty = this.#getSelectedProperty();
		const valuationMap =
			ContactFinancesAndValuation.#getValuationMap(selectedProperty);
		const canViewQuantariumAvm =
			this.props.canViewQuantariumAvm ??
			this.props.canViewAvms ??
			this.#hasTier(STARTER_TIER);
		const canViewHkAvm =
			this.props.canViewHkAvm ?? this.#hasTier(STANDARD_TIER);
		const valuations = ContactFinancesAndValuation.#filterValuationMap(
			valuationMap,
			canViewQuantariumAvm,
			canViewHkAvm
		);
		const canViewFinanceWidgets =
			this.props.canViewFinanceWidgets ?? this.#hasTier(STANDARD_TIER);
		const canViewMortgage =
			this.props.canViewMortgage ?? this.#hasTier(STARTER_TIER);

		if (!this.props.homeownerId) {
			return null;
		}

		return (
			<section className="mt-10 mb-8 border-t border-grey-300 pt-8">
				<header className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
					<h2 className="h-ms-5">
						{translate(
							'agent.pages.contacts.edit.finances_valuation.title'
						)}
					</h2>
					{this.#renderHeaderAction()}
				</header>

				{this.props.isGettingProperties ? (
					ContactFinancesAndValuation.#renderLoadingSections()
				) : (
					<React.Fragment key={this.props.selectedPropertyId}>
						{this.#renderPropertyValuationSection(
							valuations,
							selectedProperty,
							canViewQuantariumAvm,
							canViewHkAvm
						)}
						{canViewMortgage
							? this.#renderMortgageInformation(selectedProperty)
							: this.#renderMortgageUpgradeSection()}
						{canViewFinanceWidgets
							? this.#renderFinanceWidgets(selectedProperty)
							: this.#renderFinanceWidgetsUpgradeSection()}
					</React.Fragment>
				)}
			</section>
		);
	}
}
